import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { fetchCustomersList, fetchWalletTransactionsList } from "../../shared/customerMastersApi";
import { IconEye, IconRefresh } from "../../shared/toolbarIcons";
import { RecordViewDrawer } from "../../shared/RecordDrawer";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "../../shared/useClientPagination";

function formatMoney(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const dataGridSx = {
  border: "none",
  fontFamily: "inherit",
  fontSize: "0.9rem",
  height: "100%",
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#f2fcf5",
    borderBottom: "1px solid #e8ecea",
    fontWeight: 600
  },
  "& .MuiDataGrid-footerContainer": {
    borderTop: "1px solid #e8ecea",
    backgroundColor: "#fbfffc"
  },
  "& .MuiDataGrid-row:nth-of-type(even):not(.MuiDataGrid-row--editing):not(:hover)": {
    backgroundColor: "#fcfffd"
  },
  "& .MuiDataGrid-cell": {
    borderBottomColor: "#e8ecea"
  }
};

export function WalletTransactionsList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewRow, setViewRow] = useState(null);

  const customerById = useMemo(() => {
    const m = new Map();
    for (const c of customers) m.set(c.id, c);
    return m;
  }, [customers]);

  const rows = useMemo(() => {
    return items.map((x) => {
      const customer = customerById.get(x.customerId);
      const debit = Number(x.debit ?? 0);
      const credit = Number(x.credit ?? 0);
      return {
        id: x.id,
        item: x,
        customerName: customer?.name ?? `#${x.customerId}`,
        debit: formatMoney(debit),
        credit: formatMoney(credit),
        net: formatMoney(credit - debit)
      };
    });
  }, [items, customerById]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [tRes, cRes] = await Promise.all([fetchWalletTransactionsList(), fetchCustomersList()]);
    if (!tRes.ok) {
      setError(tRes.error ?? "Failed to load wallet transactions");
      setItems([]);
    } else {
      setItems(tRes.items);
    }
    if (!cRes.ok) {
      setCustomers([]);
    } else {
      setCustomers(cRes.items);
    }
    setLoading(false);
  }, []);

  const columns = useMemo(
    () => [
      {
        field: "customerName",
        headerName: "Customer",
        flex: 1,
        minWidth: 140,
        renderCell: (params) => {
          const x = params.row.item;
          return (
            <button
              type="button"
              className="btn btn-ghost"
              style={{ padding: "0.15rem 0", minHeight: 0, justifyContent: "flex-start", textDecoration: "underline" }}
              onClick={() => navigate(`/wallet-transactions?customer=${x.customerId}`)}
              title="View this customer's transactions"
            >
              {params.row.customerName}
            </button>
          );
        }
      },
      { field: "debit", headerName: "Debit", width: 110 },
      { field: "credit", headerName: "Credit", width: 110 },
      { field: "net", headerName: "Net", width: 110 },
      {
        field: "actions",
        headerName: "Actions",
        width: 90,
        align: "center",
        headerAlign: "center",
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <div className="table-actions">
            <button className="icon-button" type="button" title="View" onClick={() => setViewRow(params.row)}>
              <IconEye />
            </button>
          </div>
        )
      }
    ],
    [navigate]
  );

  useEffect(() => {
    void load();
  }, [load]);

  const total = rows.length;

  return (
    <div className="card">
      <div className="card-header card-header--row">
        <div>
          <h2>Wallet transactions</h2>
          <p>{loading ? "Loading..." : `Total: ${total}`}</p>
        </div>
        <div className="card-header-actions">
          <button
            className="icon-button icon-button--toolbar"
            type="button"
            onClick={() => void load()}
            disabled={loading}
            title="Refresh"
            aria-label="Refresh"
          >
            <IconRefresh />
          </button>
        </div>
      </div>
      <div className="card-body">
        {error && <div className="error-banner">{error}</div>}
        <div className="masters-data-grid-shell">
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            disableRowSelectionOnClick
            pagination
            paginationMode="client"
            pageSizeOptions={DEFAULT_PAGE_SIZE_OPTIONS}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: DEFAULT_PAGE_SIZE_OPTIONS[0] } }
            }}
            sx={dataGridSx}
          />
        </div>
      </div>
      <RecordViewDrawer
        open={Boolean(viewRow)}
        onClose={() => setViewRow(null)}
        label="Wallet transaction details"
        title={viewRow?.customerName ?? "—"}
        subtitle={viewRow?.net != null ? `Net: ${viewRow.net}` : undefined}
        ariaLabel="Wallet transaction details"
        sections={
          viewRow
            ? [
                {
                  title: "Transaction Details",
                  columns: 2,
                  fields: [
                    ["Customer", viewRow.customerName, true],
                    ["Debit", viewRow.debit],
                    ["Credit", viewRow.credit],
                    ["Net", viewRow.net],
                    ["Balance", formatMoney(viewRow.item.balance)],
                    ["Customer Id", viewRow.item.customerId ?? "—"]
                  ]
                }
              ]
            : []
        }
      />
    </div>
  );
}
